import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  actionsColumn,
  hideColumn,
  textColumn,
  tooltipColumn
} from "../../components/tableComponent/columns";
import TableComponent from "../../components/tableComponent";
import {
  callApiAsync,
  getBillingUnits,
  getBusinessUnits,
  getPackage,
  packageCreateOrUpdate,
  changePackageStatus
} from "../../services/apiService";
import AlertComponent from "../../components/alertModal";
import ToastNotification from "../../components/toastNotification";
import ButtonComponent from "../../components/button";
import icons from "../../constants/icons";
import StatusPill from "../../components/statusPill";
import SearchableSelectInput from "../../components/selectInput/searchableSelectInput";
import { transformToSelectData } from "../../utils/selectsValues";
import { useForm } from "react-hook-form";
import EditPackageModal from "./editPackageModal";
import { Grid } from "@mui/material";
import { Autorenew as AutorenewIcon } from "@mui/icons-material";

const PackagesView = () => {
  const userData = useSelector((state) => state.user?.userData);
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [billingUnits, setBillingUnits] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);

  const [selectedRow, setSelectedRow] = useState({});
  const [openCreateEditModal, setOpenCreateEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [statusChangeRow, setStatusChangeRow] = useState(null);

  const [toastMessage, setToastMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("warning");

  const { control, watch, setValue, reset } = useForm({
    defaultValues: {
      idBillingUnit: "",
      idBusinessUnit: ""
    }
  });

  const watchBillingUnit = watch("idBillingUnit");
  const watchBusinessUnit = watch("idBusinessUnit");

  // Cargar datos iniciales
  const loadBillingUnits = () => {
    callApiAsync(getBillingUnits, (data) => {
      const selectData = transformToSelectData(data);
      setBillingUnits(selectData);
    });
  };

  const loadBusinessUnits = () => {
    callApiAsync(getBusinessUnits, (data) => {
      setBusinessUnits(data);
    });
  };

  const loadPackages = () => {
    callApiAsync(
      () => getPackage(false),
      (data) => {
        setPackages(data);
      }
    );
  };

  useEffect(() => {
    loadBillingUnits();
    loadBusinessUnits();
    loadPackages();
  }, []);

  // Filtrar businessUnits cuando cambia billingUnit
  useEffect(() => {
    if (watchBillingUnit) {
      const filtered = businessUnits.filter(
        (bu) => String(bu.idBillingUnit) === String(watchBillingUnit)
      );
      const selectData = transformToSelectData(filtered);
      setFilteredBusinessUnits(selectData);
      setValue("idBusinessUnit", "");
      setFilteredPackages([]);
    } else {
      setFilteredBusinessUnits([]);
      setValue("idBusinessUnit", "");
      setFilteredPackages([]);
    }
  }, [watchBillingUnit, businessUnits, setValue]);

  // Filtrar packages cuando cambia businessUnit
  useEffect(() => {
    if (watchBusinessUnit) {
      const filtered = packages.filter(
        (pkg) => String(pkg.idBusinessUnit) === String(watchBusinessUnit)
      );
      setFilteredPackages(filtered);
    } else {
      setFilteredPackages([]);
    }
  }, [watchBusinessUnit, packages]);

  const handleCreate = () => {
    setSelectedRow({});
    setIsEditMode(false);
    setOpenCreateEditModal(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setIsEditMode(true);
    setOpenCreateEditModal(true);
  };

  const handleCloseModal = () => {
    setOpenCreateEditModal(false);
    setSelectedRow({});
    setIsEditMode(false);
  };

  const handleStatusChange = (row) => {
    setStatusChangeRow(row);
    setAlertOpen(true);
  };

  const confirmStatusChange = () => {
    if (statusChangeRow) {
      const newStatus = statusChangeRow.status === 1 ? 0 : 1;
      const statusData = {
        id: statusChangeRow.id,
        status: newStatus
      };

      callApiAsync(
        () => changePackageStatus(statusData),
        () => {
          setToastSeverity("success");
          setToastMessage(
            `El estado del paquete se cambió a ${newStatus === 1 ? "Activo" : "Inactivo"} exitosamente.`
          );
          setToastOpen(true);
          loadPackages();
        },
        (error) => {
          setToastSeverity("error");
          setToastMessage(error);
          setToastOpen(true);
        }
      );
    }
    setAlertOpen(false);
    setStatusChangeRow(null);
  };

  const handleSavePackage = (formData) => {
    const isCreating = !formData.id || formData.id === 0;
    const payload = { ...formData, userEmail: userData?.email ?? "" };
    callApiAsync(
      () => packageCreateOrUpdate(payload),
      () => {
        setToastSeverity("success");
        setToastMessage(
          isCreating
            ? "El paquete se creó exitosamente."
            : "El paquete se actualizó exitosamente."
        );
        setToastOpen(true);
        loadPackages();
        handleCloseModal();
      },
      (error) => {
        setToastSeverity("error");
        setToastMessage(error);
        setToastOpen(true);
      }
    );
  };

  const actionBtn = ({ row }) => (
    <div className="HContainer">
      <ButtonComponent
        onClick={() => handleEdit(row.original)}
        variant="contained"
        backgroundColor="#fff0"
        title="Editar paquete"
      >
        <img className="btn-icon w-icon-15" src={icons.blue_edit} alt="Editar" />
      </ButtonComponent>
      <ButtonComponent
        onClick={() => handleStatusChange(row.original)}
        variant="contained"
        backgroundColor="#fff0"
        title="Cambiar estado"
      >
        <AutorenewIcon
          sx={{
            fontSize: 20,
            color: row.original.status === 1 ? "#1976d2" : "#757575"
          }}
        />
      </ButtonComponent>
    </div>
  );

  const columns = useMemo(
    () => [
      hideColumn("id", "id"),
      textColumn("name", "Nombre del paquete", 200),
      textColumn("mainProductName", "Producto principal", 180),
      tooltipColumn("description", "Descripción", 300),
      {
        id: "status",
        header: "Estado",
        size: 100,
        Cell: ({ row }) => (
          <StatusPill
            backgroundColor={row.original.status === 1 ? "#4ADE80" : "#D9D9D9"}
            textColor={"#000000"}
          >
            {row.original.status === 1 ? "Activo" : "Inactivo"}
          </StatusPill>
        )
      },
      actionsColumn(actionBtn, 120)
    ],
    []
  );

  return (
    <div>
      <h1>Gestión de Paquetes</h1>

      <Grid container spacing={2} className="mb-20">
        <Grid item xs={12} md={6}>
          <SearchableSelectInput
            name="idBillingUnit"
            control={control}
            label="Unidad de Facturación"
            options={billingUnits}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SearchableSelectInput
            name="idBusinessUnit"
            control={control}
            label="Unidad de Negocio"
            options={filteredBusinessUnits}
            disabled={!watchBillingUnit}
          />
        </Grid>
      </Grid>

      {watchBusinessUnit && (
        <div>
          <TableComponent
            columns={columns}
            data={filteredPackages}
            enableGlobalFilter={true}
            initialState={{
              sorting: [
                {
                  id: "name",
                  desc: false
                }
              ]
            }}
            headerButtons={() => (
              <ButtonComponent
                onClick={handleCreate}
                type="button"
                variant="contained"
                backgroundColor="#F6C079"
                textColor="#000000"
                margin="15px 0"
              >
                <div className="HContainer a-center">
                  <img
                    className="btn-icon icon-margin-r-15"
                    src={icons.plus}
                    alt="icon"
                  />
                  <p>Crear Paquete</p>
                </div>
              </ButtonComponent>
            )}
          />
        </div>
      )}

      {!watchBusinessUnit && (
        <div className="HContainer j-center mt-40">
          <p style={{ color: "#666", fontSize: "16px" }}>
            Seleccione una Unidad de Facturación y una Unidad de Negocio para
            ver los paquetes.
          </p>
        </div>
      )}

      <EditPackageModal
        rowData={selectedRow}
        open={openCreateEditModal}
        onClose={handleCloseModal}
        onSubmitFunction={handleSavePackage}
        isEditMode={isEditMode}
        selectedBusinessUnitId={watchBusinessUnit}
      />

      <AlertComponent
        open={alertOpen}
        onClose={() => {
          setAlertOpen(false);
          setStatusChangeRow(null);
        }}
        onConfirm={confirmStatusChange}
        title="Confirmar Cambio de Estado"
        message={`¿Estás seguro de que deseas ${statusChangeRow?.status === 1 ? "desactivar" : "activar"} el paquete "${statusChangeRow?.name}"?`}
        severity="warning"
      />

      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </div>
  );
};

export default PackagesView;
